import React, { useState, useEffect } from 'react';
import './RosterView.css';
import { dowLong } from '../utils/dayOfWeek';

const API_BASE = 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';

// Helper: Parse a date string (YYYY-MM-DD) as local date, not UTC
const parseLocalDate = (dateString) => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Helper: Format a date object to YYYY-MM-DD string using local time
const formatLocalDate = (date) => {
  if (!date || isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Render "TeamA (10) vs TeamB (8)" with the higher-scoring team in bold.
// If either score is missing or the scores are tied, no emphasis is added.
// Mirrors the matchup formatting on Game Planner so the rosters look the
// same way once a game has finished.
const renderMatchup = (team1, team2, s1, s2) => {
  const bothScored = s1 != null && s2 != null;
  const team1Wins = bothScored && s1 > s2;
  const team2Wins = bothScored && s2 > s1;
  const winnerStyle = { fontWeight: 800 };
  const loserStyle = { fontWeight: 400 };
  const team1Style = team1Wins ? winnerStyle : (team2Wins ? loserStyle : null);
  const team2Style = team2Wins ? winnerStyle : (team1Wins ? loserStyle : null);
  return (
    <>
      <span style={team1Style}>{team1}{s1 != null ? ` (${s1})` : ''}</span>
      {' vs '}
      <span style={team2Style}>{team2}{s2 != null ? ` (${s2})` : ''}</span>
    </>
  );
};

export default function RosterView() {
  // State management
  // Note: the Email Roster section moved to the Message page on 20 May 2026
  // so the email recipient-mode / selection state has been removed here.
  const [weekStartDate, setWeekStartDate] = useState('');
  const [rosterByPerson, setRosterByPerson] = useState({});
  const [allPeople, setAllPeople] = useState([]);
  const [allGamesRaw, setAllGamesRaw] = useState([]); // Raw games for court view
  const [coachingRoster, setCoachingRoster] = useState({}); // Coaching roster by person
  const [showPay, setShowPay] = useState(false);
  const [viewMode, setViewMode] = useState('day'); // 'day', 'person', 'court', or 'coaching'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [personFilter, setPersonFilter] = useState(''); // Empty = show all
  const [currentTime, setCurrentTime] = useState(new Date()); // For Up Next auto-advance

  // Timer for auto-advancing "Up Next" section every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Calculate default week Monday on mount
  useEffect(() => {
    try {
      const monday = getDefaultWeekMonday();
      console.log('Setting initial week start date:', monday);
      setWeekStartDate(monday);
    } catch (err) {
      console.error('Error in initial date calculation:', err);
      setError('Failed to initialize: ' + err.message);
    }
  }, []);

  // Load roster when week start date changes
  useEffect(() => {
    if (weekStartDate) {
      loadRosterForWeek();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStartDate]);

  // Helper: Get default Monday (previous Monday or current if today is Monday)
  const getDefaultWeekMonday = () => {
    try {
      const today = new Date();
      const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

      // Calculate days to subtract to get to Monday
      const daysBack = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

      const monday = new Date(today);
      monday.setDate(monday.getDate() - daysBack);

      return formatLocalDate(monday);
    } catch (err) {
      console.error('Error calculating Monday:', err);
      return formatLocalDate(new Date());
    }
  };

  // Helper: Calculate week end date (Sunday)
  const getWeekEndDate = (startDate) => {
    if (!startDate) return '';
    const date = parseLocalDate(startDate);
    if (!date || isNaN(date.getTime())) return '';
    date.setDate(date.getDate() + 6);
    return formatLocalDate(date);
  };

  // Load all people
  const fetchAllPeople = async () => {
    try {
      const response = await fetch(`${API_BASE}/person`);
      if (response.ok) {
        const people = await response.json();
        setAllPeople(people.filter(p => p.isActive));
      }
    } catch (err) {
      console.error('Error fetching people:', err);
    }
  };

  // Load roster for the week
  const loadRosterForWeek = async () => {
    setLoading(true);
    setError('');

    try {
      console.log('Loading roster for week starting:', weekStartDate);

      // Fetch all people first
      await fetchAllPeople();
      console.log('Fetched people, now loading games...');

      // Fetch games for each day of the week (Mon-Sun)
      const allGames = [];
      const date = parseLocalDate(weekStartDate);

      for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
        const currentDate = new Date(date);
        currentDate.setDate(currentDate.getDate() + dayOffset);
        const dateStr = formatLocalDate(currentDate);

        try {
          const response = await fetch(`${API_BASE}/games/by-day/${dateStr}`);
          if (response.ok) {
            const games = await response.json();
            console.log(`Games for ${dateStr}:`, games.length);
            allGames.push(...games.map(g => ({ ...g, gameDate: dateStr })));
          } else {
            console.warn(`Failed to fetch games for ${dateStr}: ${response.status}`);
          }
        } catch (dayErr) {
          console.error(`Error fetching games for ${dateStr}:`, dayErr);
        }
      }

      console.log('Total games loaded:', allGames.length);
      setAllGamesRaw(allGames);
      // Aggregate by person
      aggregateRosterByPerson(allGames);

      // Load coaching roster for the week
      await loadCoachingRoster(weekStartDate, getWeekEndDate(weekStartDate));

      setSuccess('');
    } catch (err) {
      console.error('Error loading roster:', err);
      setError('Failed to load roster: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load coaching roster for the week
  const loadCoachingRoster = async (startDate, endDate) => {
    try {
      const response = await fetch(`${API_BASE}/coaching-roster?startDate=${startDate}&endDate=${endDate}`);
      if (response.ok) {
        const data = await response.json();
        // Aggregate by coach person
        const roster = {};
        const weekStart = parseLocalDate(startDate);
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

        data.forEach(row => {
          if (!roster[row.coachId]) {
            roster[row.coachId] = {
              person: { id: row.coachId, name: row.coachName },
              dailyAssignments: []
            };
            // Initialize 7-day array
            for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
              const d = new Date(weekStart);
              d.setDate(d.getDate() + dayOffset);
              roster[row.coachId].dailyAssignments.push({
                date: formatLocalDate(d),
                dayName: dayNames[d.getDay()],
                assignments: []
              });
            }
          }

          // Parse the program start date to find which day slot it belongs to
          const progDate = new Date(String(row.startDateTime).replace(/Z$/, '').replace(/[+-]\d{2}:\d{2}$/, ''));
          const progDateStr = formatLocalDate(progDate);
          const daySlot = roster[row.coachId].dailyAssignments.find(d => d.date === progDateStr);
          if (daySlot) {
            const progHours = progDate.getHours();
            const progMinutes = progDate.getMinutes();
            const ampm = progHours >= 12 ? 'PM' : 'AM';
            const hour12 = progHours === 0 ? 12 : progHours > 12 ? progHours - 12 : progHours;
            const timeStr = `${hour12}:${String(progMinutes).padStart(2, '0')} ${ampm}`;

            daySlot.assignments.push({
              programId: row.programId,
              programTypeName: row.programTypeName,
              location: row.location || '',
              time: timeStr,
              durationMinutes: row.durationMinutes,
              qualificationLevel: row.qualificationLevel || '',
              payPerHour: parseFloat(row.payPerHour) || 0,
              attended: row.attended
            });
          }
        });

        setCoachingRoster(roster);
      }
    } catch (err) {
      console.error('Error loading coaching roster:', err);
    }
  };

  // Transform flat games array into person-centric view
  const aggregateRosterByPerson = (games) => {
    const roster = {};

    games.forEach(game => {
      // Build a flat list of every assignee for this game, drawn from the
      // arrays returned by the API (one row per filled slot).
      const assignees = [
        ...((game.referees || []).map(s => ({ ...s, roleType: 'referee', roleLabel: `referee${s.slotOrder}` }))),
        ...((game.benchOfficials || []).map(s => ({ ...s, roleType: 'benchOfficial', roleLabel: `benchOfficial${s.slotOrder}` })))
      ];

      assignees.forEach(assignee => {
        if (!assignee.personId) return;
        if (!roster[assignee.personId]) {
          const personDetails = allPeople.find(p => p.id === assignee.personId) || { id: assignee.personId, name: assignee.personName, email: '' };
          roster[assignee.personId] = {
            person: personDetails,
            dailyAssignments: {}
          };
        }

        const dateStr = game.gameDate;
        if (!roster[assignee.personId].dailyAssignments[dateStr]) {
          roster[assignee.personId].dailyAssignments[dateStr] = [];
        }

        const pay = assignee.pay !== undefined ? parseFloat(assignee.pay) || 0 : 0;
        roster[assignee.personId].dailyAssignments[dateStr].push({
          gameId: game.id,
          team1: game.team1,
          team2: game.team2,
          team1Score: game.team1Score,
          team2Score: game.team2Score,
          scheduledTime: game.scheduledTime,
          originalScheduledTime: game.originalScheduledTime,
          division: game.division,
          role: assignee.roleLabel,
          pay: pay,
          competition: game.competition || 'N/A'
        });
      });
    });

    // Convert dailyAssignments object to array format
    const formattedRoster = {};
    Object.keys(roster).forEach(personId => {
      const dailyArr = [];
      const dateObj = {};

      // Collect all dates
      Object.keys(roster[personId].dailyAssignments).forEach(date => {
        dateObj[date] = roster[personId].dailyAssignments[date];
      });

      // Build array for each day of week
      const startDate = parseLocalDate(weekStartDate);
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

      for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
        const currentDate = new Date(startDate);
        currentDate.setDate(currentDate.getDate() + dayOffset);
        const dateStr = formatLocalDate(currentDate);
        const dayName = dayNames[currentDate.getDay()];

        dailyArr.push({
          date: dateStr,
          dayName: dayName,
          assignments: dateObj[dateStr] || []
        });
      }

      formattedRoster[personId] = {
        person: roster[personId].person,
        dailyAssignments: dailyArr
      };
    });

    setRosterByPerson(formattedRoster);
  };

  // Handle week navigation
  const goToPreviousWeek = () => {
    const date = parseLocalDate(weekStartDate);
    date.setDate(date.getDate() - 7);
    setWeekStartDate(formatLocalDate(date));
  };

  const goToNextWeek = () => {
    const date = parseLocalDate(weekStartDate);
    date.setDate(date.getDate() + 7);
    setWeekStartDate(formatLocalDate(date));
  };

  // Handle date picker change - auto-correct to Monday
  const handleDateChange = (e) => {
    const selectedDate = parseLocalDate(e.target.value);
    if (!selectedDate) return;
    const dayOfWeek = selectedDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const daysBack = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const monday = new Date(selectedDate);
    monday.setDate(monday.getDate() - daysBack);
    setWeekStartDate(formatLocalDate(monday));
  };

  // Email-roster handling moved to the Message page (Message → "Send each
  // person their roster") on 20 May 2026. Use that page to send rosters
  // via email or SMS with a preview-before-send step.

  // Format time for display — shows original time unless actual differs by 15+ minutes
  const formatTime = (datetime, originalDatetime) => {
    if (!datetime) return '--';
    const date = new Date(datetime);

    if (originalDatetime) {
      const original = new Date(originalDatetime);
      const diffMinutes = Math.abs(date.getTime() - original.getTime()) / (1000 * 60);
      if (diffMinutes < 15) {
        return original.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
      }
    }

    return date.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  // Get people with assignments this week
  let peopleWithAssignments = Object.keys(rosterByPerson)
    .map(personId => rosterByPerson[personId].person)
    .sort((a, b) => a.name.localeCompare(b.name));

  // Apply person filter
  if (personFilter) {
    peopleWithAssignments = peopleWithAssignments.filter(p => p.id.toString() === personFilter);
  }

  const weekEndDate = getWeekEndDate(weekStartDate);

  return (
    <div className="roster-view" style={{ minHeight: '400px' }}>
      {/* Header Controls */}
      <div className="roster-header">
        <div className="roster-header-row">
          <div className="week-selector">
            <button onClick={goToPreviousWeek}>&larr; Previous Week</button>
            <div className="date-display">
              <label>Week of:</label>
              <input
                type="date"
                value={weekStartDate}
                onChange={handleDateChange}
              />
              <span className="week-range">
                {weekStartDate && weekEndDate && (
                  <>
                    <strong>{dowLong(weekStartDate)}</strong>
                    {' '}—{' '}
                    {new Date(weekEndDate).toLocaleDateString('en-AU')}
                  </>
                )}
              </span>
            </div>
            <button onClick={goToNextWeek}>Next Week &rarr;</button>
          </div>
          {/* Digital brochure — static page served from /brochure.html */}
          <a
            href="/brochure.html"
            target="_blank"
            rel="noopener noreferrer"
            title="What is SwishIQ? Open the platform tour"
            style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0.35rem 0.9rem', borderRadius: 999, border: '1px solid #A4161A', color: '#A4161A', textDecoration: 'none', fontWeight: 700, fontSize: '0.82rem', whiteSpace: 'nowrap' }}
          >
            ⓘ About SwishIQ
          </a>
        </div>

        <div className="roster-header-row">
          <div className="view-toggle-section">
            <button
              className={`view-toggle-btn ${viewMode === 'day' ? 'active' : ''}`}
              onClick={() => setViewMode('day')}
            >
              Day
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'person' ? 'active' : ''}`}
              onClick={() => setViewMode('person')}
            >
              Person
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'court' ? 'active' : ''}`}
              onClick={() => setViewMode('court')}
            >
              Court
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'coaching' ? 'active' : ''}`}
              onClick={() => setViewMode('coaching')}
              style={viewMode === 'coaching' ? { background: '#A4161A', borderColor: '#A4161A' } : {}}
            >
              Coaching
            </button>
          </div>

          {viewMode === 'person' && (
            <div className="filter-section">
              <label htmlFor="person-filter">Filter by Person:</label>
              <select
                id="person-filter"
                value={personFilter}
                onChange={(e) => setPersonFilter(e.target.value)}
                style={{ padding: '0.5rem 0.75rem', borderRadius: '4px', border: '1px solid #ddd' }}
              >
                <option value="">All People</option>
                {Object.keys(rosterByPerson)
                  .map(personId => rosterByPerson[personId].person)
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(person => (
                    <option key={person.id} value={person.id}>
                      {person.name}
                    </option>
                  ))}
              </select>
            </div>
          )}

          <div className="toggle-section">
            <label>
              <input
                type="checkbox"
                checked={showPay}
                onChange={(e) => setShowPay(e.target.checked)}
              />
              Show Pay Breakdown
            </label>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      {loading && <div className="loading">Loading roster...</div>}

      {/* Roster Display */}
      {!loading && viewMode === 'person' && peopleWithAssignments.length === 0 ? (
        <div className="no-roster">No assignments this week</div>
      ) : viewMode === 'person' ? (
        <div className="roster-container">
          {/* Roster Cards */}
          <div className="person-cards">
            {peopleWithAssignments.map(person => {
              const personData = rosterByPerson[person.id];
              const hasAssignments = personData.dailyAssignments.some(day => day.assignments.length > 0);

              if (!hasAssignments) return null;

              let weeklyTotal = 0;
              personData.dailyAssignments.forEach(day => {
                day.assignments.forEach(assignment => {
                  weeklyTotal += parseFloat(assignment.pay) || 0;
                });
              });

              return (
                <div key={person.id} className="person-card">
                  <div className="card-header">
                    <div className="person-info">
                      <h3>{person.name}</h3>
                      <p className="person-email">{person.email}</p>
                    </div>
                  </div>

                  <div className="week-grid">
                    {personData.dailyAssignments.map(day => (
                      <div key={day.date} className="day-cell">
                        <div className="day-header">
                          <strong>{day.dayName}</strong>
                          <span className="day-date">{new Date(day.date).toLocaleDateString('en-AU', { month: 'short', day: 'numeric' })}</span>
                        </div>
                        <div className="assignments-list">
                          {day.assignments.length === 0 ? (
                            <div className="no-assignment">--</div>
                          ) : (
                            day.assignments.map((assignment, idx) => (
                              <div key={idx} className="assignment">
                                <div className="time">{formatTime(assignment.scheduledTime, assignment.originalScheduledTime)}</div>
                                <div className="game">{renderMatchup(assignment.team1, assignment.team2, assignment.team1Score, assignment.team2Score)}</div>
                                <div className="role">
                                  {assignment.role.includes('referee') ? 'Referee' : 'Bench Official'}
                                </div>
                                {showPay && (
                                  <div className="pay">${(parseFloat(assignment.pay) || 0).toFixed(2)}</div>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {showPay && weeklyTotal > 0 && (
                    <div className="card-footer">
                      <span>Weekly Total:</span>
                      <span className="weekly-total">${weeklyTotal.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : viewMode === 'court' ? (
        /* Court View — Time-based with Up Next */
        <div className="court-view">
          {(() => {
            // Hide any court that has zero games anywhere in the visible
            // week so the grid doesn't render dead columns. Keeps the
            // canonical order from ALL_COURTS for consistency week to
            // week. Falls back to ALL_COURTS if no games loaded yet.
            const ALL_COURTS = ['Court 1', 'Court 2', 'Court 3', 'Court 4', 'Beaton'];
            const COURTS = (allGamesRaw.length === 0)
              ? ALL_COURTS
              : ALL_COURTS.filter(c => allGamesRaw.some(g => String(g.court).trim() === c));
            const startDate = parseLocalDate(weekStartDate);
            const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            const weekDays = [];
            for (let i = 0; i < 7; i++) {
              const d = new Date(startDate);
              d.setDate(d.getDate() + i);
              weekDays.push({
                date: formatLocalDate(d),
                dayName: dayNames[d.getDay()],
                shortDate: d.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' })
              });
            }

            // Build court -> date -> games lookup
            const courtDateGames = {};
            COURTS.forEach(court => { courtDateGames[court] = {}; });
            allGamesRaw.forEach(game => {
              const court = game.court || 'Unknown';
              if (!courtDateGames[court]) courtDateGames[court] = {};
              if (!courtDateGames[court][game.gameDate]) courtDateGames[court][game.gameDate] = [];
              courtDateGames[court][game.gameDate].push(game);
            });

            // "Up Next" logic: for each court, find the next upcoming game
            const now = currentTime;
            const upNextGames = [];
            COURTS.forEach(court => {
              // Collect all games for this court across the week, sorted by time
              const allCourtGames = [];
              weekDays.forEach(day => {
                (courtDateGames[court]?.[day.date] || []).forEach(game => {
                  allCourtGames.push({ ...game, court });
                });
              });
              allCourtGames.sort((a, b) => new Date(a.scheduledTime) - new Date(b.scheduledTime));

              // Find the first game whose scheduledTime + 10 min is still in the future
              const nextGame = allCourtGames.find(game => {
                const gameTime = new Date(game.scheduledTime);
                const cutoff = new Date(gameTime.getTime() + 10 * 60 * 1000);
                return cutoff > now;
              });

              if (nextGame) {
                upNextGames.push(nextGame);
              }
            });

            // Get display time — prefer originalScheduledTime unless actual differs by 15+ min
            const getDisplayTime = (scheduledTime, originalScheduledTime) => {
              if (!scheduledTime) return '';
              const d = new Date(scheduledTime);
              if (originalScheduledTime) {
                const orig = new Date(originalScheduledTime);
                const diffMin = Math.abs(d.getTime() - orig.getTime()) / (1000 * 60);
                if (diffMin < 15) {
                  return orig.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
                }
              }
              return d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
            };

            return (
              <div className="court-grid-container">
                {/* Up Next Section */}
                {upNextGames.length > 0 && (
                  <div className="up-next-section">
                    <div className="up-next-header">
                      <h3>Up Next</h3>
                    </div>
                    <div className="up-next-tiles">
                      {upNextGames.map((game, idx) => {
                        const gameTime = new Date(game.scheduledTime);
                        const timeStr = getDisplayTime(game.scheduledTime, game.originalScheduledTime);
                        const isNow = gameTime <= now && new Date(gameTime.getTime() + 10 * 60 * 1000) > now;
                        return (
                          <div key={idx} className={`up-next-tile ${isNow ? 'up-next-now' : ''}`}>
                            <div className="up-next-court">{game.court}</div>
                            <div className="up-next-time">{timeStr}</div>
                            <div className="up-next-game">{renderMatchup(game.team1, game.team2, game.team1Score, game.team2Score)}</div>
                            <div className="up-next-division">{game.division}</div>
                            {(game.referees || []).length > 0 && (
                              <div className="up-next-refs">
                                {(game.referees || []).filter(r => r.personName).map(r => r.refereeCode ? `${r.refereeCode} ${r.personName}` : r.personName).join(', ')}
                              </div>
                            )}
                            {isNow && <div className="up-next-live-badge">LIVE</div>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Court Tables — time down the left side */}
                {COURTS.map(court => {
                  // Check if this court has any games this week
                  const hasGames = weekDays.some(day => (courtDateGames[court]?.[day.date] || []).length > 0);
                  if (!hasGames) return null;

                  // Only show days that have games for this court
                  const activeDays = weekDays.filter(day => (courtDateGames[court]?.[day.date] || []).length > 0);

                  // Collect all unique time slots for this court across active days
                  const timeSlotSet = new Set();
                  activeDays.forEach(day => {
                    (courtDateGames[court]?.[day.date] || []).forEach(game => {
                      timeSlotSet.add(getDisplayTime(game.scheduledTime, game.originalScheduledTime));
                    });
                  });
                  const timeSlots = Array.from(timeSlotSet).sort();

                  return (
                    <div key={court} className="court-table-card">
                      <div className="card-header">
                        <div className="person-info">
                          <h3>{court}</h3>
                        </div>
                      </div>
                      <div className="court-time-table-wrapper">
                        <table className="court-time-table">
                          <thead>
                            <tr>
                              <th className="court-time-col">Time</th>
                              {activeDays.map(day => (
                                <th key={day.date} className="court-day-col">
                                  <div>{day.dayName}</div>
                                  <div className="court-day-date">{day.shortDate}</div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {timeSlots.map(timeSlot => (
                              <tr key={timeSlot}>
                                <td className="court-time-cell">{timeSlot}</td>
                                {activeDays.map(day => {
                                  const games = (courtDateGames[court]?.[day.date] || [])
                                    .filter(g => getDisplayTime(g.scheduledTime, g.originalScheduledTime) === timeSlot);
                                  return (
                                    <td key={day.date} className="court-game-cell">
                                      {games.length === 0 ? (
                                        <span className="court-empty">—</span>
                                      ) : (
                                        games.map((game, idx) => (
                                          <div key={idx} className="court-game-entry">
                                            <div className="court-game-teams">{renderMatchup(game.team1, game.team2, game.team1Score, game.team2Score)}</div>
                                            <div className="court-game-division">{game.division}</div>
                                            {(game.referees || []).length > 0 && (
                                              <div className="court-game-refs">
                                                {(game.referees || []).filter(r => r.personName).map(r => r.refereeCode ? `${r.refereeCode} ${r.personName}` : r.personName).join(', ')}
                                              </div>
                                            )}
                                            {(game.benchOfficials || []).length > 0 && (
                                              <div className="court-game-bench">
                                                {(game.benchOfficials || []).map(b => b.personName).filter(Boolean).join(', ')}
                                              </div>
                                            )}
                                            {showPay && (
                                              <div className="court-game-pay">
                                                ${(
                                                  [...(game.referees || []), ...(game.benchOfficials || [])]
                                                    .reduce((sum, s) => sum + (parseFloat(s.pay) || 0), 0)
                                                ).toFixed(2)}
                                              </div>
                                            )}
                                          </div>
                                        ))
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      ) : viewMode === 'day' ? (
        /* Day View — each day as a table with time rows and court columns */
        <div className="day-view">
          {(() => {
            // Hide courts with no games this week (same approach as Court
            // view — keeps the canonical order so the operator's eye
            // doesn't have to relearn the layout when a court reopens).
            const ALL_COURTS = ['Court 1', 'Court 2', 'Court 3', 'Court 4', 'Beaton'];
            const COURTS = (allGamesRaw.length === 0)
              ? ALL_COURTS
              : ALL_COURTS.filter(c => allGamesRaw.some(g => String(g.court).trim() === c));
            const startDate = parseLocalDate(weekStartDate);
            const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const now = currentTime;

            // Build all 7 days of the week
            const weekDays = [];
            for (let i = 0; i < 7; i++) {
              const d = new Date(startDate);
              d.setDate(d.getDate() + i);
              weekDays.push({
                date: formatLocalDate(d),
                dayName: dayNames[d.getDay()],
                fullDate: d.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
                dateObj: d
              });
            }

            // Split into "past this week" (rolled-up by default) and
            // "today + future" (always shown). When the entire week is in
            // the past (looking back at last week), show every day.
            const pastDays = weekDays.filter(day => day.dateObj < today);
            const futureDays = weekDays.filter(day => day.dateObj >= today);
            const daysToShow = futureDays.length > 0 ? futureDays : weekDays;

            // Build court -> date -> games lookup
            const courtDateGames = {};
            COURTS.forEach(court => { courtDateGames[court] = {}; });
            allGamesRaw.forEach(game => {
              const court = game.court || 'Unknown';
              if (!courtDateGames[court]) courtDateGames[court] = {};
              if (!courtDateGames[court][game.gameDate]) courtDateGames[court][game.gameDate] = [];
              courtDateGames[court][game.gameDate].push(game);
            });

            // Get display time
            const getDisplayTime = (scheduledTime, originalScheduledTime) => {
              if (!scheduledTime) return '';
              const d = new Date(scheduledTime);
              if (originalScheduledTime) {
                const orig = new Date(originalScheduledTime);
                const diffMin = Math.abs(d.getTime() - orig.getTime()) / (1000 * 60);
                if (diffMin < 15) {
                  return orig.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
                }
              }
              return d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
            };

            // Check if a day is today
            const isToday = (dateObj) => {
              const t = new Date();
              return dateObj.getFullYear() === t.getFullYear() &&
                     dateObj.getMonth() === t.getMonth() &&
                     dateObj.getDate() === t.getDate();
            };

            // "Up Next" banner — for each court, find the next upcoming game
            // across the visible (today + future) days. Same shape as the
            // Court view's banner so the look is consistent.
            const upNextGames = [];
            COURTS.forEach(court => {
              const allCourtGames = [];
              daysToShow.forEach(day => {
                (courtDateGames[court]?.[day.date] || []).forEach(game => {
                  allCourtGames.push({ ...game, court });
                });
              });
              allCourtGames.sort((a, b) => new Date(a.scheduledTime) - new Date(b.scheduledTime));
              const nextGame = allCourtGames.find(game => {
                const gameTime = new Date(game.scheduledTime);
                const cutoff = new Date(gameTime.getTime() + 10 * 60 * 1000);
                return cutoff > now;
              });
              if (nextGame) upNextGames.push(nextGame);
            });

            // Per-day section renderer. Shared between the rolled-up past
            // days and the always-visible today + future days.
            const renderDaySection = (day) => {
              const activeCourts = COURTS.filter(court =>
                (courtDateGames[court]?.[day.date] || []).length > 0
              );

              if (activeCourts.length === 0) {
                return (
                  <div key={day.date} className={`day-section ${isToday(day.dateObj) ? 'day-section-today' : ''}`}>
                    <div className="day-section-header">
                      <h3>{day.fullDate}</h3>
                      {isToday(day.dateObj) && <span className="today-badge">Today</span>}
                    </div>
                    <div className="day-no-games">No games scheduled</div>
                  </div>
                );
              }

              const timeSlotSet = new Set();
              activeCourts.forEach(court => {
                (courtDateGames[court]?.[day.date] || []).forEach(game => {
                  timeSlotSet.add(getDisplayTime(game.scheduledTime, game.originalScheduledTime));
                });
              });
              const timeSlots = Array.from(timeSlotSet).sort();

              let upNextTimeSlot = null;
              if (isToday(day.dateObj)) {
                const todayGames = [];
                activeCourts.forEach(court => {
                  (courtDateGames[court]?.[day.date] || []).forEach(game => {
                    todayGames.push({
                      scheduledTime: new Date(game.scheduledTime),
                      displayTime: getDisplayTime(game.scheduledTime, game.originalScheduledTime)
                    });
                  });
                });
                const slotEarliestTime = {};
                todayGames.forEach(g => {
                  if (!slotEarliestTime[g.displayTime] || g.scheduledTime < slotEarliestTime[g.displayTime]) {
                    slotEarliestTime[g.displayTime] = g.scheduledTime;
                  }
                });
                for (const slot of timeSlots) {
                  const slotTime = slotEarliestTime[slot];
                  if (slotTime) {
                    const cutoff = new Date(slotTime.getTime() + 10 * 60 * 1000);
                    if (cutoff > now) { upNextTimeSlot = slot; break; }
                  }
                }
              }

              return (
                <div key={day.date} className={`day-section ${isToday(day.dateObj) ? 'day-section-today' : ''}`}>
                  <div className="day-section-header">
                    <h3>{day.fullDate}</h3>
                    {isToday(day.dateObj) && <span className="today-badge">Today</span>}
                  </div>
                  <div className="day-table-wrapper">
                    <table className="day-table">
                      <thead>
                        <tr>
                          <th className="day-time-col">Time</th>
                          {activeCourts.map(court => (
                            <th key={court} className="day-court-col">{court}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {timeSlots.map(timeSlot => {
                          const isUpNext = timeSlot === upNextTimeSlot;
                          return (
                            <tr key={timeSlot} className={isUpNext ? 'day-row-upnext' : ''}>
                              <td className={`day-time-cell ${isUpNext ? 'day-time-cell-upnext' : ''}`}>
                                {timeSlot}
                                {isUpNext && <span className="day-upnext-badge">UP NEXT</span>}
                              </td>
                              {activeCourts.map(court => {
                                const games = (courtDateGames[court]?.[day.date] || [])
                                  .filter(g => getDisplayTime(g.scheduledTime, g.originalScheduledTime) === timeSlot);
                                return (
                                  <td key={court} className={`day-game-cell ${isUpNext ? 'day-game-cell-upnext' : ''}`}>
                                    {games.length === 0 ? (
                                      <span className="day-empty">—</span>
                                    ) : (
                                      games.map((game, idx) => (
                                        <div key={idx} className={`day-game-entry ${isUpNext ? 'day-game-entry-upnext' : ''}`}>
                                          <div className="day-game-teams">{renderMatchup(game.team1, game.team2, game.team1Score, game.team2Score)}</div>
                                          <div className="day-game-division">{game.division}</div>
                                          {(game.referees || []).length > 0 && (
                                            <div className="day-game-refs">
                                              {(game.referees || []).filter(r => r.personName).map(r => r.refereeCode ? `${r.refereeCode} ${r.personName}` : r.personName).join(', ')}
                                            </div>
                                          )}
                                          {(game.benchOfficials || []).length > 0 && (
                                            <div className="day-game-bench">
                                              {(game.benchOfficials || []).map(b => b.personName).filter(Boolean).join(', ')}
                                            </div>
                                          )}
                                          {showPay && (
                                            <div className="day-game-pay">
                                              ${(
                                                [...(game.referees || []), ...(game.benchOfficials || [])]
                                                  .reduce((sum, s) => sum + (parseFloat(s.pay) || 0), 0)
                                              ).toFixed(2)}
                                            </div>
                                          )}
                                        </div>
                                      ))
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            };

            return (
              <div className="day-grid-container">
                {/* Up Next Section (mirrors the Court view) */}
                {upNextGames.length > 0 && (
                  <div className="up-next-section">
                    <div className="up-next-header">
                      <h3>Up Next</h3>
                    </div>
                    <div className="up-next-tiles">
                      {upNextGames.map((game, idx) => {
                        const gameTime = new Date(game.scheduledTime);
                        const timeStr = getDisplayTime(game.scheduledTime, game.originalScheduledTime);
                        const isNow = gameTime <= now && new Date(gameTime.getTime() + 10 * 60 * 1000) > now;
                        return (
                          <div key={idx} className={`up-next-tile ${isNow ? 'up-next-now' : ''}`}>
                            <div className="up-next-court">{game.court}</div>
                            <div className="up-next-time">{timeStr}</div>
                            <div className="up-next-game">{renderMatchup(game.team1, game.team2, game.team1Score, game.team2Score)}</div>
                            <div className="up-next-division">{game.division}</div>
                            {(game.referees || []).length > 0 && (
                              <div className="up-next-refs">
                                {(game.referees || []).filter(r => r.personName).map(r => r.refereeCode ? `${r.refereeCode} ${r.personName}` : r.personName).join(', ')}
                              </div>
                            )}
                            {isNow && <div className="up-next-live-badge">LIVE</div>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Past days within this week - rolled up by default. */}
                {pastDays.length > 0 && futureDays.length > 0 && (
                  <details className="past-days-rollup" style={{ marginBottom: '1rem' }}>
                    <summary style={{ cursor: 'pointer', padding: '0.5rem 0.75rem', background: '#f0f0f0', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.85rem', color: '#555', fontWeight: 500 }}>
                      Show past day{pastDays.length === 1 ? '' : 's'} this week ({pastDays.length})
                    </summary>
                    <div style={{ marginTop: '0.5rem' }}>
                      {pastDays.map(renderDaySection)}
                    </div>
                  </details>
                )}

                {daysToShow.map(renderDaySection)}
              </div>
            );
          })()}
        </div>
      ) : viewMode === 'coaching' ? (
        /* Coaching View */
        <div className="coaching-view">
          {(() => {
            const coachIds = Object.keys(coachingRoster).sort((a, b) =>
              coachingRoster[a].person.name.localeCompare(coachingRoster[b].person.name)
            );

            if (coachIds.length === 0) {
              return <div className="no-roster">No coaching assignments this week</div>;
            }

            return (
              <>
                <div className="person-cards">
                  {coachIds.map(coachId => {
                    const coachData = coachingRoster[coachId];
                    const hasAssignments = coachData.dailyAssignments.some(day => day.assignments.length > 0);
                    if (!hasAssignments) return null;

                    let weeklyPay = 0;
                    coachData.dailyAssignments.forEach(day => {
                      day.assignments.forEach(a => {
                        weeklyPay += (a.payPerHour || 0) * (a.durationMinutes / 60);
                      });
                    });

                    return (
                      <div key={coachId} className="person-card" style={{ borderLeft: '4px solid #A4161A' }}>
                        <div className="card-header">
                          <div className="person-info">
                            <h3>{coachData.person.name}</h3>
                          </div>
                        </div>
                        <div className="week-grid">
                          {coachData.dailyAssignments.map(day => (
                            <div key={day.date} className="day-cell">
                              <div className="day-header">
                                <strong>{day.dayName}</strong>
                                <span className="day-date">{new Date(day.date + 'T00:00:00').toLocaleDateString('en-AU', { month: 'short', day: 'numeric' })}</span>
                              </div>
                              <div className="assignments-list">
                                {day.assignments.length === 0 ? (
                                  <div className="no-assignment">--</div>
                                ) : (
                                  day.assignments.map((assignment, idx) => (
                                    <div key={idx} className="assignment" style={{ borderLeft: '2px solid #A4161A' }}>
                                      <div className="time">{assignment.time}</div>
                                      <div className="game" style={{ color: '#A4161A', fontWeight: 600 }}>{assignment.programTypeName}</div>
                                      {assignment.location && (
                                        <div className="role" style={{ fontSize: '0.65rem', color: '#666' }}>{assignment.location}</div>
                                      )}
                                      <div className="role" style={{ fontSize: '0.65rem', color: '#888' }}>
                                        {assignment.durationMinutes} min
                                        {assignment.qualificationLevel && ` | ${assignment.qualificationLevel}`}
                                      </div>
                                      {showPay && assignment.payPerHour > 0 && (
                                        <div className="pay">${(assignment.payPerHour * (assignment.durationMinutes / 60)).toFixed(2)}</div>
                                      )}
                                    </div>
                                  ))
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        {showPay && weeklyPay > 0 && (
                          <div className="card-footer">
                            <span>Weekly Total:</span>
                            <span className="weekly-total">${weeklyPay.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </div>
      ) : null}
    </div>
  );
}
